<template>
  <component-wrapper v-if="mountedNonDialog">
    <div class="page-registration-form">
      <div v-if="registrationLight && isValidateToken">
        <loading-indicator
          variant="spinnerLocal"
          class="registration-form__loading"
        />
      </div>

      <div
        v-else-if="(registrationLight && !invitationToken) || (registrationLight && invalidToken)"
        class="registration-form__token-error"
      >
        <grid-container-fi>
          <div
            class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-4 lg:fi-col-end-10"
          >
            <div class="registration-form__result-status">
              <result-status-fi :error="invalidTokenError" />
            </div>
          </div>
        </grid-container-fi>
      </div>

      <template v-else>
        <transition leave-active-class="animated fadeOut">
          <div
            v-if="!formSuccess"
            v-intersect="onIntersectModule"
            class="registration-form"
          >
            <template v-if="fields.headline?.value">
              <grid-container-fi>
                <div
                  class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-3 lg:fi-col-end-11"
                >
                  <headline-fi
                    class="registration-form__headline"
                    tag="h1"
                    :size="60"
                  >
                    {{ fields.headline.value }}
                  </headline-fi>
                </div>
              </grid-container-fi>

              <section-divider-small-fi />
            </template>

            <grid-container-fi :container="false">
              <div class="fi-col-span-full">
                <div
                  ref="registrationFormHead"
                  class="registration-form__head"
                  :class="overflowClasses"
                >
                  <ul class="fi-mb-0">
                    <li
                      v-intersect="onOverflowLeft"
                      class="registration-form__detector-left"
                    />
                    <li
                      v-for="(headline, index) in stepHeadlines"
                      :key="index"
                      :class="getStepStateClass(index)"
                      class="registration-form__head-item"
                      @click="changeStep(index)"
                    >
                      <p class="mb-0">
                        <span class="registration-form__head-item-number">
                          {{ index + 1 }}
                        </span>
                        <span class="registration-form__head-item-title">
                          {{ headline }}
                        </span>
                      </p>
                    </li>
                    <li
                      v-intersect="onOverflowRight"
                      class="registration-form__detector-right"
                    />
                  </ul>
                </div>
              </div>
            </grid-container-fi>

            <grid-container-fi>
              <div
                class="fi-col-start-1 fi-col-end-13 sm:fi-col-start-2 sm:fi-col-end-12 lg:fi-col-start-3 lg:fi-col-end-11"
              >
                <form ref="registrationForm">
                  <registration-light
                    v-if="registrationLight"
                    :current-step="currentStep"
                    @valid-changed-step-contact-data="(value) => (stepContactDataValid = value)"
                    @valid-changed-step-password="(value) => (stepPasswordValid = value)"
                  />
                  <registration-without-target-group
                    v-else-if="registrationWithoutTargetGroup"
                    :current-step="currentStep"
                    @step-type-changed="(value) => stepTypeChanged(value)"
                    @valid-changed-step-contact-data="(value) => (stepContactDataValid = value)"
                    @valid-changed-step-address="(value) => (stepAddressValid = value)"
                    @valid-changed-step-password="(value) => (stepPasswordValid = value)"
                  />
                  <registration-default
                    v-else
                    :current-step="currentStep"
                    @step-type-changed="(value) => stepTypeChanged(value)"
                    @valid-changed-step-target-group="(value) => (stepTargetGroupValid = value)"
                    @valid-changed-step-contact-data="(value) => (stepContactDataValid = value)"
                    @valid-changed-step-address="(value) => (stepAddressValid = value)"
                    @valid-changed-step-password="(value) => (stepPasswordValid = value)"
                  />
                </form>
              </div>
            </grid-container-fi>

            <grid-container-fi>
              <div class="fi-col-span-full">
                <div
                  v-intersect="onIntersectControls"
                  class="registration-form__actions"
                >
                  <div
                    class="registration-form__actions-container"
                    :class="
                      stickyContentsEnabled ? 'registration-form__actions-container--sticky' : null
                    "
                  >
                    <div class="registration-form__actions__button-wrapper">
                      <loading-indicator-wrapper
                        :loading="isLoading || false"
                        dark
                        variant="spinnerLocal"
                        spinoff="in-relative-centered"
                        class="registration-form__spinner"
                      >
                        <div class="registration-form__button-wrapper">
                          <button-fi
                            class="button-fi__primary--large"
                            type="submit"
                            :disabled="isNextButtonDisabled"
                            data-test="registration-next-step"
                            @click.prevent.stop="nextStep"
                          >
                            <template v-if="currentStep < maxSteps">
                              {{ fields.buttonNextStep?.value }}
                            </template>
                            <template v-else>
                              {{ fields.buttonSubmit?.value }}
                            </template>
                          </button-fi>
                        </div>
                      </loading-indicator-wrapper>
                    </div>
                  </div>
                </div>
              </div>
            </grid-container-fi>

            <template v-if="Boolean(fields.verifyAddress?.value)">
              <confirm-user-address-dialog-fi
                v-if="googleAddress"
                ref="confirmUserAddressDialog"
                :google-address="googleAddress"
                @keep-address="keepAddress"
              />

              <GMapMap
                v-show="false"
                ref="gmap"
              />
            </template>
          </div>
        </transition>

        <transition enter-active-class="animated fadeIn delay-1s">
          <grid-container-fi v-if="formSuccess">
            <div
              class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-4 lg:fi-col-end-10"
            >
              <div class="registration-form__result-status">
                <result-status-fi :headline="fields.successEmailHasBeenSentBoxTitle">
                  <p
                    v-if="fields.successMessage?.value"
                    class="fi-mb-30"
                  >
                    {{ fields.successMessage.value }}
                  </p>

                  <div class="button-container">
                    <button-fi
                      v-if="hasNecessaryLinkDetails(getLink('homeLink'))"
                      class="button-fi__primary--large button-fi--stretched fi-mb-24 fi-mt-24"
                      type="button"
                      @click="goTo(getLink('homeLink').url)"
                    >
                      {{ getLink('homeLink').text }}
                    </button-fi>
                  </div>
                </result-status-fi>
              </div>
            </div>
          </grid-container-fi>
        </transition>
      </template>
    </div>
  </component-wrapper>
</template>

<script lang="ts">
import { ButtonFi, HeadlineFi, LoadingIndicator, LoadingIndicatorWrapper } from 'atoms/index';
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper.vue';
import GMapMap from 'components/google-maps/GMapMap';
import GridContainerFi from 'components/grid-fi/GridContainerFi.vue';
import ConfirmUserAddressDialogFi from 'components/registration/confirm-user-address-dialog/ConfirmUserAddressDialogFi.vue';
import RegistrationDefault from 'components/registration/fischer/RegistrationDefault.vue';
import RegistrationLight from 'components/registration/fischer/RegistrationLight.vue';
import RegistrationWithoutTargetGroup from 'components/registration/fischer/RegistrationWithoutTargetGroup.vue';
import ResultStatusFi from 'components/result-status-fi/ResultStatusFi.vue';
import GET_INVITATION_BY_TOKEN from 'gql/getInvitationByToken.gql';
import REGISTER from 'gql/register.gql';
import consentMixin from 'mixins/consentMixin.vue';
import eventHubMixin from 'mixins/eventHubMixin.vue';
import SectionDividerSmallFi from 'pages/section-divider-fi/SectionDividerSmallFi.vue';

import type { FischerPlugin } from '@/lib/fischerPlugin';
import currentBreakpoint from '@/utils/breakpoint';
import { getLinkData, hasNecessaryLinkDetails } from '@/utils/linkHelper';
import { scrollToElement } from '@/utils/pageScrolling';

export default {
  name: 'MyAccountRegister',
  components: {
    ButtonFi,
    LoadingIndicatorWrapper,
    LoadingIndicator,
    GridContainerFi,
    ComponentWrapper,
    RegistrationDefault,
    RegistrationLight,
    RegistrationWithoutTargetGroup,
    ResultStatusFi,
    HeadlineFi,
    SectionDividerSmallFi,
    ConfirmUserAddressDialogFi,
    GMapMap,
  },
  mixins: [consentMixin, eventHubMixin],
  inject: ['fields'],
  data() {
    return {
      formSuccess: false,
      registrationFailed: false,
      errorCode: false,
      isLoading: false,
      currentStep: 1,
      currentStepType: null,
      classDone: 'registration-form__head-item--done',
      classActive: 'registration-form__head-item--active',
      errors: 0,
      isRegistrationVisible: false,
      isButtonVisible: false,
      showShadowLeft: false,
      showShadowRight: false,
      stepTargetGroupValid: false,
      stepContactDataValid: false,
      stepAddressValid: false,
      stepPasswordValid: false,
      invitationToken: null,
      invalidToken: false,
      isValidateToken: false,
      googleAddress: null,
      geoCoder: null,
      checkAddress: false,
      mountedNonDialog: false,
    };
  },
  computed: {
    maxSteps() {
      if (this.registrationLight) {
        return 2;
      }

      return this.registrationWithoutTargetGroup ? 3 : 4;
    },
    isNextButtonDisabled() {
      if (this.currentStep === 1) {
        if (this.registrationLight || this.registrationWithoutTargetGroup) {
          return !this.stepContactDataValid;
        }

        return !this.stepTargetGroupValid;
      }

      if (this.currentStep === 2) {
        if (this.registrationLight) {
          return !this.stepPasswordValid;
        }

        if (this.registrationWithoutTargetGroup) {
          return !this.stepAddressValid;
        }

        return !this.stepContactDataValid;
      }

      if (this.currentStep === 3) {
        if (this.registrationWithoutTargetGroup) {
          return !this.stepPasswordValid;
        }

        return !this.stepAddressValid;
      }

      if (this.currentStep === 4) {
        return !this.stepPasswordValid;
      }

      return true;
    },
    registrationType() {
      return this.fields.registrationType?.value || 'Default';
    },
    registrationLight() {
      return this.fields.registrationType?.value === 'Light';
    },
    registrationWithoutTargetGroup() {
      return this.fields.registrationType?.value === 'WithoutTargetGroup';
    },
    stickyContentsEnabled() {
      return this.isRegistrationVisible && !this.isButtonVisible;
    },
    overflowClasses() {
      let classes = '';

      if (this.showShadowLeft) {
        classes += 'registration-form__head--shadow-left ';
      }
      if (this.showShadowRight) {
        classes += 'registration-form__head--shadow-right ';
      }

      return classes;
    },
    isXsBreakpoint() {
      return currentBreakpoint() === 'screen-xs';
    },
    stepHeadlines() {
      if (this.registrationLight) {
        return [
          this.fields.headlineStep2?.value || 'Step 2',
          this.fields.headlineStep4?.value || 'Step 4',
        ];
      }

      if (this.registrationWithoutTargetGroup) {
        return [
          this.fields.headlineStep2?.value || 'Step 2',
          this.fields.headlineStep3?.value || 'Step 3',
          this.fields.headlineStep4?.value || 'Step 4',
        ];
      }

      return [
        this.fields.headlineStep1?.value || 'Step 1',
        this.fields.headlineStep2?.value || 'Step 2',
        this.fields.headlineStep3?.value || 'Step 3',
        this.fields.headlineStep4?.value || 'Step 4',
      ];
    },
    // Step 1
    targetGroup() {
      return this.$store.getters['registration/getTargetGroup'];
    },
    targetGroupTitle() {
      return this.$store.getters['registration/getTargetGroupTitle'];
    },
    // Step 2
    salutation() {
      return this.$store.getters['registration/getSalutation'];
    },
    title() {
      return this.$store.getters['registration/getTitle'];
    },
    firstname() {
      return this.$store.getters['registration/getFirstname'];
    },
    lastname() {
      return this.$store.getters['registration/getLastname'];
    },
    email: {
      get() {
        return this.$store.getters['registration/getEmail'];
      },
      set(email) {
        this.$store.dispatch('registration/setEmail', email);
      },
    },
    phone() {
      return this.$store.getters['registration/getPhone'];
    },
    // Step 3
    subTargetGroup() {
      return this.$store.getters['registration/getSubTargetGroup'];
    },
    subTargetGroupTitle() {
      return this.$store.getters['registration/getSubTargetGroupTitle'];
    },
    country() {
      return this.$store.getters['registration/getCountry'];
    },
    company() {
      return this.$store.getters['registration/getCompany'];
    },
    customerNumber() {
      return this.$store.getters['registration/getCustomerNumber'];
    },
    street() {
      return this.$store.getters['registration/getStreet'];
    },
    streetNumber() {
      return this.$store.getters['registration/getStreetNumber'];
    },
    zipcode() {
      return this.$store.getters['registration/getZipcode'];
    },
    city() {
      return this.$store.getters['registration/getCity'];
    },
    // Step 4
    password() {
      return this.$store.getters['registration/getPassword'];
    },
    passwordRepeat() {
      return this.$store.getters['registration/getPasswordRepeat'];
    },
    termsAndConditions() {
      return this.$store.getters['registration/getTermsAndConditions'];
    },
    advertisingConsent() {
      return this.$store.getters['registration/getAdvertisingConsent'];
    },
    isCaptchaError() {
      return this.$store.getters['registration/getIsCaptchaError'];
    },
    captchaResponse: {
      get() {
        return this.$store.getters['registration/getCaptchaResponse'];
      },
      set(captchaResponse) {
        this.$store.dispatch('registration/setCaptchaResponse', captchaResponse);
      },
    },
    invalidTokenError() {
      return {
        title: this.fields.invalidTokenTitle?.value,
        message: this.fields.invalidTokenMessage?.value,
      };
    },
  },
  mounted() {
    if (
      !this.$fischer.query('token') &&
      this.$fischer.isLoginDialogEnabled() &&
      (!IS_SHOWROOM || window.confirm('redirect to login dialog?'))
    ) {
      const page: keyof FischerPlugin['pages'] = 'registration';
      window.location.href = `${this.$fischer.hostUrlBase()}/${this.$fischer.siteLanguage().toLowerCase()}#${page}-dialog`;
    } else {
      this.mountedNonDialog = true;
    }
  },
  created() {
    let initStep = 'target-group';
    if (this.registrationLight || this.registrationWithoutTargetGroup) {
      initStep = 'contact-data';
    }

    if (IS_SSR) {
      return;
    }

    this.checkAddress = Boolean(this.fields.verifyAddress?.value);

    const invitationToken = this.$fischer.query('token');
    if (this.registrationLight && invitationToken) {
      this.validateToken(invitationToken);
    }

    this.sentStepEvent(initStep);
  },
  methods: {
    hasNecessaryLinkDetails,
    async submitForm() {
      if (
        (this.registrationWithoutTargetGroup || this.registrationLight
          ? false
          : !this.stepTargetGroupValid) ||
        !this.stepContactDataValid ||
        (this.registrationLight ? false : !this.stepAddressValid) ||
        !this.stepPasswordValid
      ) {
        console.error('form not valid');
        return;
      }

      this.isLoading = true;
      this.registrationFailed = false;

      try {
        const consentTemplates = await this.getRegistrationConsentTemplates();
        const { data } = await this.$apollo.mutate({
          mutation: REGISTER,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            host: this.$fischer.hostname(),
            // Step target group
            targetGroup: this.targetGroup,
            // Step contact data
            salutation: this.salutation,
            title: this.title,
            firstName: this.firstname,
            lastName: this.lastname,
            email: this.email,
            phoneNumber: this.phone,
            // Step address
            subTargetGroup: this.subTargetGroup,
            country: this.country,
            companyName: this.company,
            customerNumber: this.customerNumber,
            streetName: this.street,
            streetNumber: this.streetNumber,
            zipCode: this.zipcode,
            city: this.city,
            // Step password
            password: this.password,
            passwordValidation: this.passwordRepeat,
            consentTemplates,
            captchaResponse: this.captchaResponse,
            // URL Params
            lang: this.$fischer.language_639_1(),
            invitationToken: this.invitationToken,
          },
        });

        const { statusCode, errorCode } = data.register;
        const isSuccessCase = statusCode === 201 && !errorCode;

        if (isSuccessCase) {
          const layerData = {
            event: 'myfischerRegistration',
            UserGroup: this.targetGroupTitle,
          };

          if (this.targetGroup === 'GROUP-craftsman' && this.subTargetGroupTitle) {
            layerData.UserCraft = this.subTargetGroupTitle;
          }

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(layerData);
          this.sendEvent('custom', {}, 'registration_success', {
            kind: this.registrationType,
            step: this.fields.headlineStep4?.value,
          });
        }

        this.$smoothScroll({
          scrollTo: document.body,
          duration: 500,
          updateHistory: false,
          offset: 0,
        });

        this.isLoading = false;
        this.formSuccess = isSuccessCase;
        if (!isSuccessCase) {
          this.errorCode = errorCode;
          this.enterErrorState(errorCode);
        }
      } catch (error) {
        this.enterErrorState(error);
      }
    },
    getStepStateClass(index) {
      let stateClass = '';

      if (index + 1 === this.currentStep) {
        stateClass = this.classActive;
      } else if (index + 1 <= this.currentStep) {
        stateClass = this.classDone;
      }
      return stateClass;
    },
    changeStep(index) {
      if (this.getStepStateClass(index) === this.classDone && index + 1 < this.currentStep) {
        this.currentStep = index + 1;
        this.captchaResponse = '';
      }
    },
    nextStep() {
      if (this.currentStep < this.maxSteps) {
        if (this.currentStepType === 'address' && this.checkAddress) {
          this.verifyAddress();
        } else {
          this.currentStep += 1;
          this.scrollMobileTabs();
          scrollToElement(this.$refs.registrationFormHead, 'center');
          this.checkAddress = Boolean(this.fields.verifyAddress?.value);
        }
      } else {
        this.submitForm();
      }
    },
    scrollMobileTabs() {
      if (currentBreakpoint() === 'screen-xs' || currentBreakpoint() === 'screen-sm') {
        setTimeout(() => {
          const targetTab = this.$refs.registrationFormHead.querySelector(
            '.registration-form__head-item--active',
          );
          const targetTabPosition =
            this.$refs.registrationFormHead.clientWidth -
            (targetTab.offsetLeft + targetTab.offsetWidth) +
            4;

          if (targetTabPosition < 0) {
            this.$refs.registrationFormHead.scrollLeft = targetTabPosition * -1;
          }
        }, 20);
      }
    },
    onIntersectModule(observer) {
      this.isRegistrationVisible =
        observer[0].isIntersecting && observer[0].boundingClientRect.top > 0;
    },
    onIntersectControls(observer) {
      this.isButtonVisible = observer[0].isIntersecting && observer[0].boundingClientRect.top > 0;
    },
    onOverflowLeft(entries, observer, isIntersecting) {
      this.showShadowLeft = !isIntersecting;
    },
    onOverflowRight(entries, observer, isIntersecting) {
      this.showShadowRight = !isIntersecting;
    },
    getLink(name) {
      return getLinkData(this.fields, name);
    },
    goTo(url) {
      if (url) {
        window.location.href = url;
      }
    },
    enterErrorState(error) {
      console.error(error);
      this.registrationFailed = true;
      this.isLoading = false;

      if (this.errorCode === 'DuplicateUidError') {
        let action = {};

        if (
          this.fields.errorAlreadyRegisteredMessageLink?.value &&
          (this.fields.errorAlreadyRegisteredMessageLink.value.url ||
            this.fields.errorAlreadyRegisteredMessageLink.value.href) &&
          this.fields.errorAlreadyRegisteredMessageLink.value.text
        ) {
          action = {
            label: this.fields.errorAlreadyRegisteredMessageLink.value.text,
            clicked: this.goTo(
              this.fields.errorAlreadyRegisteredMessageLink.value.url ||
                this.fields.errorAlreadyRegisteredMessageLink.value.href,
            ),
            closeOnClick: true,
          };
        }

        this.$store.dispatch('notifications/openToastNotification', {
          message: {
            value: this.fields.errorAlreadyRegisteredMessage?.value,
          },
          type: 'error',
          icon: 'alert-triangle',
          persist: true,
          close: true,
          action,
        });
      } else {
        this.$store.dispatch('notifications/openGeneralErrorNotification', { vm: this });
      }
    },
    async validateToken(token) {
      this.isValidateToken = true;
      try {
        const { data: invitation } = await this.$apollo.query({
          query: GET_INVITATION_BY_TOKEN,
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            invitationToken: token,
          },
        });
        this.email = invitation.getInvitationByToken.invitationEmail || '';
        this.invitationToken = token;

        this.isValidateToken = false;
      } catch (error) {
        this.invalidToken = true;
        this.isValidateToken = false;
      }
    },
    stepTypeChanged(value) {
      this.currentStepType = value;
      this.sentStepEvent(value);
    },
    keepAddress() {
      this.checkAddress = false;
      this.nextStep();
    },
    sentStepEvent(value) {
      this.sendEvent('custom', {}, 'registration_step', {
        kind: this.registrationType,
        step: value,
      });
    },
    async verifyAddress() {
      try {
        const userAddress = `${this.street} ${this.streetNumber}, ${this.zipcode} ${this.city}`;

        if (!this.geoCoder) {
          this.geoCoder = new window.google.maps.Geocoder();
        }

        const geocodeVars = { address: userAddress };
        if (this.country) {
          geocodeVars.componentRestrictions = {
            country: this.country,
          };
        }

        this.geoCoder.geocode(geocodeVars, async (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK) {
            const { address_components: addressComponents } = results[0];
            const streetNumber =
              addressComponents.find((el) => el.types.find((x) => x === 'street_number'))
                ?.long_name || null;
            const street =
              addressComponents.find((el) => el.types.find((x) => x === 'route'))?.long_name ||
              null;
            const town =
              addressComponents.find((el) => el.types.find((x) => x === 'sublocality'))
                ?.long_name || null;
            const city =
              addressComponents.find((el) => el.types.find((x) => x === 'locality'))?.long_name ||
              null;
            const zipcode =
              addressComponents.find((el) => el.types.find((x) => x === 'postal_code'))
                ?.long_name || null;

            let googleCity = city;
            if (town === this.city) {
              googleCity = town;
            }

            this.googleAddress = {
              streetNumber,
              street,
              city: googleCity || null,
              zipcode,
              country: this.country || null,
            };

            if (
              !(streetNumber && street && googleCity && zipcode) ||
              (streetNumber === this.streetNumber &&
                street === this.street &&
                googleCity === this.city &&
                zipcode === this.zipcode)
            ) {
              this.currentStep += 1;
              this.scrollMobileTabs();
              scrollToElement(this.$refs.registrationFormHead, 'center');
            } else {
              await this.$nextTick();
              this.$refs.confirmUserAddressDialog.open();
            }
          }
        });
      } catch (error) {
        console.error(`Error getting LatLon coords: ${error}`);
      }
    },
  },
};
</script>

<style lang="scss">
@import './my-account-registration';
</style>
